import React from 'react';
import { Chit, ChitContent, ChitMain, ChitAvatar, ChitText } from 'src/components/ui/chit';
import { getOrgTypeLabel } from 'src/utils/organizationUtils';


interface OrganizationOverviewCardProps {
  organization: {
    orgId?: string;
    name: string;
    type: string;
    memberCount?: number;
    description?: string;
    colors?: {
      primary: string;
      secondary: string;
      accent: string;
    };
    logoUrl?: string;
  };
  size?: 'default' | 'large';
}

export const OrganizationOverviewCard: React.FC<OrganizationOverviewCardProps> = ({
  organization,
  size = 'default'
}) => {
  const colors = organization.colors;
  
  
  // Convert hex to RGB for opacity support
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const uniqueId = `org-card-${organization.orgId || organization.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

  const renderCustomStyles = () => {
    if (!colors) return null;
    
    const primaryRgb = hexToRgb(colors.primary);
    const secondaryRgb = hexToRgb(colors.secondary);
    
    if (!primaryRgb || !secondaryRgb) return null;
    
    return (
      <style>
        {`
          .${uniqueId} {
            background: linear-gradient(135deg, rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2), rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)) !important;
            border-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.4) !important;
          }
          .${uniqueId} .chit-avatar {
            background-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.4) !important;
          }
        `}
      </style>
    );
  };

  const getClassName = () => {
    const sizeClasses = size === 'large' ? 'mb-10 p-8' : 'mb-8';
    const baseClasses = `${sizeClasses} border`;
    if (colors) {
      return `${baseClasses} ${uniqueId}`;
    }
    return `${baseClasses} bg-gradient-to-br from-purple-600/20 to-blue-600/20 border-purple-500/30`;
  };

  const getAvatarSize = () => {
    return size === 'large' ? 'xl' : 'lg';
  };

  const getAvatarClassName = () => {
    if (colors) {
      return "text-white chit-avatar";
    }
    return "bg-white/20 text-white";
  };

  const getTitleClassName = () => {
    return size === 'large' 
      ? "text-white text-2xl font-bold" 
      : "text-white text-xl font-semibold";
  };

  const getSubtitleClassName = () => {
    return size === 'large' 
      ? "text-white/80 text-base" 
      : "text-white/80 text-sm";
  };

  const getDescriptionClassName = () => {
    return size === 'large' 
      ? "text-white/70 text-base" 
      : "text-white/70 text-sm";
  };

  return (
    <>
      {renderCustomStyles()}
      <Chit 
        interactive={false}
        className={getClassName()}
      >
        <ChitContent>
          <ChitMain>
            <ChitAvatar 
              src={organization.logoUrl}
              fallback={organization.name.substring(0, 2)}
              size={getAvatarSize() as any}
              className={getAvatarClassName()}
            />
            <ChitText 
              title={organization.name}
              subtitle={`${getOrgTypeLabel(organization.type)}${organization.memberCount !== undefined ? ` • ${organization.memberCount} members` : ''}`}
              description={organization.description || ''}
              titleClassName={getTitleClassName()}
              subtitleClassName={getSubtitleClassName()}
              descriptionClassName={getDescriptionClassName()}
            />
          </ChitMain>
        </ChitContent>
      </Chit>
    </>
  );
};